/**
 * Spline Viewer Utility
 * Handles loading and initializing Spline 3D viewers
 */

/**
 * Loads the Spline viewer script asynchronously
 * @returns {Promise} Resolves when script is loaded or fails
 */
export const loadSplineScript = () => {
    return new Promise((resolve) => {
        // Check if script is already loaded
        if (document.querySelector('script[src*="spline-viewer"]')) {
            resolve();
            return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@splinetool/viewer@1.12.16/build/spline-viewer.js';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => resolve(); // Continue even if script fails
        document.head.appendChild(script);
    });
};

/**
 * Creates and initializes a Spline viewer in the specified container
 * @param {HTMLElement} container - The container element to append the viewer to
 * @param {string} url - The Spline scene URL
 * @returns {Promise<HTMLElement>} Promise that resolves with the spline-viewer element
 */
export const createSplineViewer = async (container, url) => {
    if (!container) {
        console.warn('Spline container not found');
        return null;
    }
    
    // Load script first
    await loadSplineScript();
    
    // Small delay to ensure script is ready
    return new Promise((resolve) => {
        setTimeout(() => {
            const splineViewer = document.createElement('spline-viewer');
            splineViewer.setAttribute('url', url);
            splineViewer.style.width = '100%';
            splineViewer.style.height = '100%';
            container.appendChild(splineViewer);
            resolve(splineViewer);
        }, 100);
    });
};

/**
 * Loads a Spline viewer in the loader container
 * @param {HTMLElement} container - The loader Spline container element
 * @param {string} url - The Spline scene URL (optional, reads from data-spline-url attribute)
 */
export const loadLoaderSpline = (container, url) => {
    if (!container) return;
    
    // Get URL from data attribute if not provided
    if (!url) {
        url = container.getAttribute('data-spline-url');
    }
    
    // If no URL found, don't load Spline
    if (!url) return;
    
    createSplineViewer(container, url).catch((error) => {
        console.error('Failed to load Spline viewer:', error);
    });
};
